import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { PracticeSignup } from '../../models/practice-signup.interface';
import { environment } from '../../../environments/environment';
import { ROUTE_CONSTANTS } from '../../util/route.constants';

type ClinicianRow = { name?: string; email?: string; hpi?: string };
type JsPdfWithAutoTable = jsPDF & { lastAutoTable?: { finalY: number } };

@Injectable({ providedIn: 'root' })
export class PracticeSignupPdfService {
  private readonly combinedLogoPath =
    `${environment.cvaUI_url.replace(/\/$/, '')}/assets/images/ih_cva_combined_logo.png`;
  private readonly logoAspectRatio = 73.5 / 382.5;
  // Match legacy wkhtmltopdf HTML styles: body 12px, h3 headings, small text 10px.
  private readonly fontSizes = {
    title: 13,
    section: 11,
    body: 9,
    small: 7.5,
  };
  private readonly spacing = {
    afterLogo: 10,
    afterTitle: 8,
    beforeTable: 5,
    afterTable: 10,
    beforeSection: 6,
    consentLine: 6,
    bodyLine: 4.5,
    signatureLine: 5,
  };
  private readonly tableStyles = {
    cellPadding: 2.5,
    minCellHeight: 8,
    lineHeight: 1.35,
  };

  constructor(private readonly http: HttpClient) {}

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    const chunkSize = 0x8000;

    for (let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }

    return btoa(binary);
  }

  private getAutoTableFinalY(doc: jsPDF, fallback: number): number {
    return (doc as JsPdfWithAutoTable).lastAutoTable?.finalY ?? fallback;
  }

  private async loadLogoPngBytes(assetPath: string): Promise<Uint8Array> {
    const buffer = await firstValueFrom(
      this.http.get(assetPath, { responseType: 'arraybuffer' }),
    );
    return new Uint8Array(buffer);
  }

  private async renderLogoHeader(doc: jsPDF, pageW: number, margin: number): Promise<number> {
    try {
      const logoBytes = await this.loadLogoPngBytes(this.combinedLogoPath);
      const logoW = pageW * 0.5;
      const logoH = logoW * this.logoAspectRatio;
      const logoX = (pageW - logoW) / 2;

      doc.addImage(logoBytes, 'PNG', logoX, margin, logoW, logoH);
      return margin + logoH + this.spacing.afterLogo;
    } catch (error) {
      console.error('[PracticeSignupPdf] Failed to load logo for PDF:', this.combinedLogoPath, error);
      return margin + 4;
    }
  }

  private formatSubmittedAt(createdAt?: string): string {
    if (!createdAt) {
      return '';
    }

    const parsed = new Date(createdAt);
    return Number.isNaN(parsed.getTime()) ? createdAt : parsed.toString();
  }

  private getPrivacyPolicyUrl(): string {
    const baseUrl = environment.cvaUI_url.replace(/\/$/, '');
    return `${baseUrl}/${ROUTE_CONSTANTS.PRIVACY_POLICY}`;
  }

  private renderPrivacyConsentLine(
    doc: jsPDF,
    margin: number,
    y: number,
    privacyPolicyUrl: string,
  ): number {
    const prefix = '• I have read and agree to the ';
    const linkText = 'Privacy Policy';
    const suffix = '.';

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(this.fontSizes.body);
    doc.setTextColor(0, 0, 0);
    doc.text(prefix, margin, y);

    const prefixWidth = doc.getTextWidth(prefix);
    doc.setTextColor(0, 0, 255);
    doc.textWithLink(linkText, margin + prefixWidth, y, { url: privacyPolicyUrl });

    const linkWidth = doc.getTextWidth(linkText);
    doc.setTextColor(0, 0, 0);
    doc.text(suffix, margin + prefixWidth + linkWidth, y);

    return y + this.spacing.consentLine;
  }

  private getTableStyles() {
    return {
      fontSize: this.fontSizes.body,
      cellPadding: this.tableStyles.cellPadding,
      minCellHeight: this.tableStyles.minCellHeight,
      lineHeight: this.tableStyles.lineHeight,
    };
  }

  private getTableHeadStyles() {
    return {
      fillColor: [240, 240, 240] as [number, number, number],
      textColor: 20,
      fontStyle: 'bold' as const,
      fontSize: this.fontSizes.body,
      cellPadding: this.tableStyles.cellPadding,
      minCellHeight: this.tableStyles.minCellHeight,
      lineHeight: this.tableStyles.lineHeight,
    };
  }

  async generatePracticeSignupPdfBase64(payload: PracticeSignup): Promise<string> {
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();

    const margin = 14;
    let y = await this.renderLogoHeader(doc, pageW, margin);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(this.fontSizes.title);
    doc.text('Practice Signup Details', pageW / 2, y, { align: 'center' });
    y += this.spacing.afterTitle;

    // Section 1: Practice manager/representative
    doc.setFontSize(this.fontSizes.section);
    doc.text('Practice Manager or Representative Information', margin, y);
    y += this.spacing.beforeTable;

    const itVendor =
      payload?.itVendor === 'Other'
        ? payload?.additionalDetails?.itVendorContact ?? ''
        : payload?.itVendor ?? '';

    const createdAt = this.formatSubmittedAt(payload?.createdAt);

    const infoRows: Array<[string, string]> = [
      ['Name', payload?.name ?? ''],
      ['Email', payload?.email ?? ''],
      ['Phone', payload?.phone ?? ''],
      ['Facility ID', payload?.practiceId ?? ''],
      ['Facility Name', payload?.practiceName ?? ''],
      ['IT Vendor', itVendor],
    ];

    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      head: [['Title', 'Details']],
      body: infoRows,
      theme: 'grid',
      headStyles: this.getTableHeadStyles(),
      styles: this.getTableStyles(),
      columnStyles: { 0: { cellWidth: 50 } },
    });

    y = this.getAutoTableFinalY(doc, y + 20) + this.spacing.afterTable;

    // Section 2: Practitioners
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(this.fontSizes.section);
    doc.text('Practitioner Details', margin, y);
    y += this.spacing.beforeTable;

    const clinicians: ClinicianRow[] = Array.isArray(payload?.clinicians) ? payload.clinicians : [];
    const clinicianBody =
      clinicians.length > 0
        ? clinicians.map((c, idx) => [
            String(idx + 1),
            c?.name ?? '',
            String(c?.hpi ?? '').toUpperCase(),
            c?.email ?? '',
          ])
        : [['1', '(none)', '', '']];

    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      head: [['Sr. No.', 'Name', 'HPI', 'Email']],
      body: clinicianBody,
      theme: 'grid',
      headStyles: this.getTableHeadStyles(),
      styles: this.getTableStyles(),
      columnStyles: { 0: { cellWidth: 18 }, 1: { cellWidth: 60 }, 2: { cellWidth: 40 }, 3: { cellWidth: 'auto' } },
    });

    y = this.getAutoTableFinalY(doc, y + 20) + this.spacing.afterTable;

    // Section 3: ACC Workflow
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(this.fontSizes.section);
    doc.text(`Register for ACC Workflow: ${payload?.additionalDetails?.enableACC ? 'Yes' : 'No'}`, margin, y);
    y += this.spacing.afterTitle;

    // Section 4: Consents
    if (y > pageH - 45) {
      doc.addPage();
      y = margin;
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(this.fontSizes.section);
    doc.text('Provided Consent', margin, y);
    y += this.spacing.beforeSection;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(this.fontSizes.body);
    y = this.renderPrivacyConsentLine(doc, margin, y, this.getPrivacyPolicyUrl());

    const usageConsent =
      '• I consent to the Clinical Virtual Assistant to be used and launched from within Medtech evolution, including all ALEX API available functionalities.';
    const wrappedUsageConsent = doc.splitTextToSize(usageConsent, pageW - margin * 2);
    doc.text(wrappedUsageConsent, margin, y, { lineHeightFactor: 1.35 });
    y += wrappedUsageConsent.length * this.spacing.bodyLine + this.spacing.afterTable;

    // Signature block
    doc.setFont('helvetica', 'bolditalic');
    doc.setFontSize(this.fontSizes.body);
    const sig = payload?.signature ?? '';
    doc.text(String(sig), pageW - margin, y, { align: 'right' });
    y += this.spacing.signatureLine;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(this.fontSizes.body);
    doc.text('Signed by (Name)', pageW - margin, y, { align: 'right' });
    y += this.spacing.signatureLine;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(this.fontSizes.small);
    doc.text(`Date and Time : ${createdAt}`, pageW - margin, y, { align: 'right' });

    return this.arrayBufferToBase64(doc.output('arraybuffer'));
  }
}

